import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { secureStorage } from "./secureStorage";

export type ThemeMode = "dark" | "light";

export type AuthUser = {
  id: string | number;
  type?: number;
  name: string;
  surname?: string;
  companyName?: string;
  profilePhoto?: string;
  adType?: number;
  isPrimaryAccount?: boolean;
  settingsId?: number;
  phoneNumber?: string;
  phonePrefix?: string;
  email?: string;
  lang?: string;
  roles: string[];
  permissions: string[];
};

export type AuthState = {
  token: string | null;
  refreshToken?: string | null;
  expiresAt?: number | null;
  user: AuthUser | null;
  roles: string[];
  permissions: string[];
};

export type UiState = {
  sidebarOpen: boolean;
  theme: ThemeMode;
};

export type DesktopState = {
  dockPinned: string[];
  dockRecent: string[];
};

export type AppState = {
  auth: AuthState;
  ui: UiState;
  desktop: DesktopState;

  authActions: {
    login: (payload: {
      token: string;
      refreshToken?: string;
      expiresAt?: number;
      user: AuthUser;
    }) => void;
    logout: () => void;
  };

  uiActions: {
    setSidebarOpen: (open: boolean) => void;
    setTheme: (theme: ThemeMode) => void;
  };

  desktopActions: {
    openApp: (key: string) => void;
    pinApp: (key: string) => void;
    unpinApp: (key: string) => void;
    clearRecent: () => void;
  };
};

const initialState = (): Pick<AppState, "auth" | "ui" | "desktop"> => ({
  auth: {
    token: null,
    refreshToken: null,
    expiresAt: null,
    user: null,
    roles: [],
    permissions: [],
  },

  ui: {
    sidebarOpen: false,
    theme: "light",
  },

  desktop: {
    dockPinned: ["overview", "users", "settings"],
    dockRecent: [],
  },
});

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState(),

        authActions: {
          login: ({ token, refreshToken, expiresAt, user }) => {
            set({
              auth: {
                token,
                refreshToken: refreshToken ?? null,
                expiresAt: expiresAt ?? null,
                user,
                roles: user.roles || [],
                permissions: user.permissions || [],
              },
            });
          },

          logout: () => {
            set({
              ...initialState(),
              authActions: get().authActions,
              uiActions: get().uiActions,
              desktopActions: get().desktopActions,
            });
          },
        },

        uiActions: {
          setSidebarOpen: (open) =>
            set({ ui: { ...get().ui, sidebarOpen: open } }),

          setTheme: (theme) => set({ ui: { ...get().ui, theme } }),
        },

        desktopActions: {
          openApp: (key) => {
            const d = get().desktop;
            const next = [key, ...(d.dockRecent || [])]
              .filter((x, i, arr) => arr.indexOf(x) === i)
              .slice(0, 8);

            set({ desktop: { ...d, dockRecent: next } });
          },

          pinApp: (key) => {
            const d = get().desktop;
            if (d.dockPinned.includes(key)) return;
            set({ desktop: { ...d, dockPinned: [...d.dockPinned, key] } });
          },

          unpinApp: (key) => {
            const d = get().desktop;
            set({
              desktop: {
                ...d,
                dockPinned: d.dockPinned.filter((x) => x !== key),
              },
            });
          },

          clearRecent: () => {
            const d = get().desktop;
            set({ desktop: { ...d, dockRecent: [] } });
          },
        },
      }),
      {
        name: "app-store",
        storage: createJSONStorage(() => ({
          getItem: (key) => secureStorage.getItem(key),
          setItem: (key, value) => secureStorage.setItem(key, value),
          removeItem: (key) => secureStorage.removeItem(key),
        })),
        partialize: (s) => ({
          auth: s.auth,
          desktop: s.desktop,
          ui: {
            theme: s.ui.theme,
          },
        }),
      }
    )
  )
);
